import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
  Alert,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' } };

const GestorSectionCard = ({
  section,
  editMode = false,
  tiposDeAccesos = [],
  onAddLink,
  onDeleteLink,
  onEditLink,
  onEditSection,
  onDeleteSection,
  onReorderLinks,
  queryActive = false,
  onSectionDragStart,
  onSectionDragOver,
  onSectionDrop,
}) => {
  const [draggedLinkIndex, setDraggedLinkIndex] = useState(null);
  const [cardMenuAnchor, setCardMenuAnchor] = useState(null);
  const [isEditingCardTitle, setIsEditingCardTitle] = useState(false);
  const [cardTitleInput, setCardTitleInput] = useState(section.title);

  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [test, setTest] = useState(false);
  const [tipoAccesoId, setTipoAccesoId] = useState('');
  const [formError, setFormError] = useState('');

  const [editingIndex, setEditingIndex] = useState(null);
  const [editName, setEditName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editTest, setEditTest] = useState(false);
  const [editTipoAccesoId, setEditTipoAccesoId] = useState('');
  const [editError, setEditError] = useState('');

  const beginEdit = (index, link) => {
    setEditingIndex(index);
    setEditName(link.name || '');
    setEditUrl(link.url === '#' ? '' : link.url || '');
    setEditTest(Boolean(link.test));
    const defaultId =
      link.tipo_acceso_id ||
      tiposDeAccesos.find((t) => t.nombre.toLowerCase() === String(link.req || 'ninguno').toLowerCase())?.id ||
      tiposDeAccesos[0]?.id ||
      '';
    setEditTipoAccesoId(defaultId);
    setEditError('');
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    if (!editName.trim() || !editUrl.trim() || !editTipoAccesoId) return;
    const linkTarget = section.links[editingIndex];
    try {
      setEditError('');
      await onEditLink?.(section.id, editingIndex, {
        id: linkTarget?.id,
        nombre: editName.trim(),
        url: editUrl.trim(),
        tarjeta_id: Number(section.id),
        tipo_acceso_id: Number(editTipoAccesoId),
        es_entorno_test: Boolean(editTest),
      });
      setEditingIndex(null);
    } catch (err) {
      setEditError(err.message || 'Error al actualizar el enlace.');
    }
  };

  const submitLink = async (event) => {
    event.preventDefault();
    if (!name.trim() || !url.trim() || !tipoAccesoId) return;
    try {
      setFormError('');
      await onAddLink?.(section.id, {
        nombre: name.trim(),
        url: url.trim(),
        tarjeta_id: Number(section.id),
        tipo_acceso_id: Number(tipoAccesoId),
        es_entorno_test: Boolean(test),
      });
      setName('');
      setUrl('');
      setTest(false);
      setTipoAccesoId('');
      setFormOpen(false);
    } catch (err) {
      setFormError(err.message || 'Error al crear el enlace.');
    }
  };

  const submitCardTitle = async (event) => {
    event.preventDefault();
    if (!cardTitleInput.trim()) return;
    await onEditSection?.(section.id, cardTitleInput.trim());
    setIsEditingCardTitle(false);
  };

  return (
    <Box
      component="section"
      draggable={editMode && !queryActive}
      onDragStart={(event) => {
        if (editMode && !queryActive) {
          event.dataTransfer.setData('text/section-id', String(section.id));
          event.dataTransfer.effectAllowed = 'move';
          onSectionDragStart?.(section.id);
        }
      }}
      onDragOver={(event) => {
        if (editMode && !queryActive) {
          event.preventDefault();
          onSectionDragOver?.(section.id);
        }
      }}
      onDrop={(event) => {
        if (editMode && !queryActive) {
          event.preventDefault();
          const sourceId = event.dataTransfer.getData('text/section-id');
          if (sourceId && String(sourceId) !== String(section.id)) {
            onSectionDrop?.(sourceId, section.id);
          }
        }
      }}
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E3EAF0',
        borderRadius: '16px',
        boxShadow: '0 3px 14px rgba(30,36,51,.045)',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        minWidth: 0,
        cursor: editMode && !queryActive ? 'grab' : 'default',
        '&:hover': {
          borderColor: 'rgba(0,154,218,.3)',
          boxShadow: '0 6px 20px rgba(30,36,51,.07)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pb: 1, borderBottom: '1px solid #EEF2F6' }}>
        {isEditingCardTitle ? (
          <Box
            component="form"
            onSubmit={submitCardTitle}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
          >
            <TextField
              size="small"
              value={cardTitleInput}
              onChange={(e) => setCardTitleInput(e.target.value)}
              autoFocus
              fullWidth
              sx={fieldSx}
            />
            <Button type="submit" size="small" variant="contained" sx={{ textTransform: 'none', borderRadius: '9px' }}>
              Guardar
            </Button>
            <Button
              type="button"
              size="small"
              onClick={() => {
                setCardTitleInput(section.title);
                setIsEditingCardTitle(false);
              }}
              sx={{ textTransform: 'none' }}
            >
              Cancelar
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                component="h2"
                sx={{ m: 0, fontSize: 18, fontWeight: 700, color: '#1E2433', overflowWrap: 'anywhere' }}
              >
                {section.title}
              </Typography>
            </Box>
            {editMode && (
              <>
                <Tooltip title="Más opciones de tarjeta">
                  <IconButton
                    size="small"
                    onClick={(event) => setCardMenuAnchor(event.currentTarget)}
                    aria-label={`Opciones de ${section.title}`}
                    sx={{ mt: '-4px', color: '#64748B', '&:hover': { color: '#007EB5', backgroundColor: '#EAF4FA' } }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={cardMenuAnchor}
                  open={Boolean(cardMenuAnchor)}
                  onClose={() => setCardMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: { minWidth: 180, borderRadius: '10px', boxShadow: '0 8px 24px rgba(30,36,51,.14)' },
                  }}
                >
                  {onEditSection && (
                    <MenuItem
                      onClick={() => {
                        setCardMenuAnchor(null);
                        setCardTitleInput(section.title);
                        setIsEditingCardTitle(true);
                      }}
                      sx={{ fontSize: 14, gap: 1 }}
                    >
                      <EditOutlinedIcon fontSize="small" /> Editar tarjeta
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      setCardMenuAnchor(null);
                      onDeleteSection?.();
                    }}
                    sx={{ color: '#D32F2F', fontSize: 14, gap: 1 }}
                  >
                    <DeleteOutlineIcon fontSize="small" /> Eliminar tarjeta
                  </MenuItem>
                </Menu>
              </>
            )}
          </>
        )}
      </Box>

      {section.links.map((link, index) => {
        const isExternal = Boolean(link.url && link.url !== '#');
        return (
          <React.Fragment key={link.id || `${section.id}-${index}-${link.name}`}>
            <Box
              draggable={editMode && !queryActive}
              onDragStart={(event) => {
                if (!queryActive) {
                  event.stopPropagation();
                  setDraggedLinkIndex(index);
                  event.dataTransfer.setData('text/link-section', String(section.id));
                  event.dataTransfer.setData('text/link-index', String(index));
                  event.dataTransfer.effectAllowed = 'move';
                }
              }}
              onDragOver={(event) => {
                if (editMode && !queryActive && draggedLinkIndex !== null) event.preventDefault();
              }}
              onDrop={(event) => {
                if (editMode && !queryActive && event.dataTransfer.getData('text/link-section')) {
                  event.preventDefault();
                  event.stopPropagation();
                  const sourceSection = event.dataTransfer.getData('text/link-section');
                  const sourceIndex = Number(event.dataTransfer.getData('text/link-index'));
                  if (
                    String(sourceSection) === String(section.id) &&
                    Number.isInteger(sourceIndex) &&
                    sourceIndex !== index
                  ) {
                    onReorderLinks?.(section.id, sourceIndex, index);
                  }
                  setDraggedLinkIndex(null);
                }
              }}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
            >
              <Box
                component="a"
                href={isExternal ? link.url : undefined}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                onClick={(e) => {
                  if (!isExternal) e.preventDefault();
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  minHeight: 40,
                  px: 1.25,
                  py: 1,
                  borderRadius: '9px',
                  backgroundColor: '#F7FAFC',
                  textDecoration: 'none',
                  color: '#1E2433',
                  '&:hover': { backgroundColor: '#EAF4FA', color: '#005A91' },
                }}
              >
                <Typography
                  component="span"
                  sx={{ fontSize: 14, fontWeight: 500, color: 'inherit', lineHeight: 1.4, overflowWrap: 'anywhere' }}
                >
                  {link.name}
                </Typography>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', flex: '0 0 auto' }}>
                  {link.test && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: '#9A3412',
                        backgroundColor: '#FFEDD5',
                        px: '7px',
                        py: '3px',
                        borderRadius: '5px',
                      }}
                    >
                      TEST
                    </Box>
                  )}
                  {((link.req && link.req !== 'Ninguno') ||
                    (link.requiresVPN && !link.req) ||
                    (link.hasReq && !link.req && !link.requiresVPN)) && (
                      <Box
                        component="span"
                        title={`Requiere ${link.req && link.req !== 'Ninguno' ? link.req : 'VPN'}`}
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#334155',
                          backgroundColor: '#E8EDF2',
                          px: '7px',
                          py: '3px',
                          borderRadius: '5px',
                        }}
                      >
                        {link.req && link.req !== 'Ninguno' ? link.req : 'VPN'}
                      </Box>
                    )}
                </Box>
              </Box>
              {editMode && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flex: '0 0 auto' }}>
                  <Tooltip title="Editar enlace">
                    <IconButton
                      size="small"
                      onClick={() => beginEdit(index, link)}
                      aria-label={`Editar ${link.name}`}
                      sx={{ color: '#007EB5' }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar enlace">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDeleteLink?.(section.id, index, link)}
                      aria-label={`Eliminar ${link.name}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            {editMode && editingIndex === index && (
              <Box
                component="form"
                onSubmit={submitEdit}
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  backgroundColor: '#F5FAFD',
                  border: '1px solid #D8EAF4',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#004582' }}>Editar enlace</Typography>
                <TextField
                  size="small"
                  label="Nombre del enlace *"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  size="small"
                  label="Dirección web (URL) *"
                  placeholder="https://..."
                  value={editUrl}
                  onChange={(e) => setEditUrl(e.target.value)}
                  required
                  type="url"
                  fullWidth
                  sx={fieldSx}
                />
                <FormControl size="small" fullWidth sx={fieldSx}>
                  <InputLabel id={`edit-acceso-label-${section.id}-${index}`} shrink>
                    Acceso adicional *
                  </InputLabel>
                  <Select
                    labelId={`edit-acceso-label-${section.id}-${index}`}
                    label="Acceso adicional *"
                    value={editTipoAccesoId}
                    onChange={(e) => setEditTipoAccesoId(Number(e.target.value))}
                    required
                  >
                    {tiposDeAccesos.map((tipo) => (
                      <MenuItem key={tipo.id} value={tipo.id}>
                        {tipo.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox size="small" checked={editTest} onChange={(e) => setEditTest(e.target.checked)} />
                  }
                  label="Es un entorno de prueba (TEST)"
                />
                {editError && <Alert severity="error">{editError}</Alert>}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
                  <Button
                    type="button"
                    size="small"
                    onClick={() => setEditingIndex(null)}
                    sx={{ textTransform: 'none' }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    sx={{ textTransform: 'none', borderRadius: '9px' }}
                  >
                    Guardar cambios
                  </Button>
                </Box>
              </Box>
            )}
          </React.Fragment>
        );
      })}

      {editMode && section.links.length === 0 && (
        <Typography sx={{ fontSize: 13, color: '#64748B', py: 1 }}>Esta tarjeta todavía no tiene enlaces.</Typography>
      )}
      {editMode && !formOpen && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
          <Button
            size="small"
            onClick={() => {
              setFormError('');
              setFormOpen(true);
            }}
            sx={{ px: 1, textTransform: 'none', fontWeight: 600, color: '#007EB5', '&:hover': { backgroundColor: '#EAF4FA' } }}
          >
            + Agregar enlace
          </Button>
        </Box>
      )}
      {editMode && formOpen && (
        <Box
          component="form"
          onSubmit={submitLink}
          sx={{
            p: 1.5,
            borderRadius: '12px',
            backgroundColor: '#F5FAFD',
            border: '1px solid #D8EAF4',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#004582' }}>Agregar enlace</Typography>
          <TextField
            size="small"
            label="Nombre del enlace *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            sx={fieldSx}
          />
          <TextField
            size="small"
            label="Dirección web (URL) *"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            type="url"
            fullWidth
            sx={fieldSx}
          />
          <FormControl size="small" fullWidth sx={fieldSx}>
            <InputLabel id={`acceso-label-${section.id}`} shrink>
              Acceso adicional *
            </InputLabel>
            <Select
              labelId={`acceso-label-${section.id}`}
              label="Acceso adicional *"
              value={tipoAccesoId}
              onChange={(e) => setTipoAccesoId(Number(e.target.value))}
              required
              displayEmpty
              renderValue={(value) => {
                const selectedTipo = tiposDeAccesos.find((t) => Number(t.id) === Number(value));
                return selectedTipo ? (
                  selectedTipo.nombre
                ) : (
                  <Box component="span" sx={{ color: '#757575' }}>
                    Seleccioná una opción
                  </Box>
                );
              }}
            >
              <MenuItem value="" disabled>
                Seleccioná una opción
              </MenuItem>
              {tiposDeAccesos.map((tipo) => (
                <MenuItem key={tipo.id} value={tipo.id}>
                  {tipo.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox size="small" checked={test} onChange={(e) => setTest(e.target.checked)} />}
            label="Es un entorno de prueba (TEST)"
          />
          {formError && <Alert severity="error">{formError}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
            <Button
              type="button"
              size="small"
              onClick={() => {
                setName('');
                setUrl('');
                setTest(false);
                setTipoAccesoId('');
                setFormError('');
                setFormOpen(false);
              }}
              sx={{ textTransform: 'none' }}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="contained" size="small" sx={{ textTransform: 'none', borderRadius: '9px' }}>
              Guardar enlace
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GestorSectionCard;
